import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { format } from 'app/shared/utils/string.util';
import { supportedMongoDbTypes } from 'app/registries/consts/tabular-consts';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { connectionString, cosmosDBPEWellknownErrorMessages, server32Options } from 'app/shared/utils/connector-metadata-static-consts';
import { dataflowCosmosDbMongoSource } from 'app/registries/dataflow/cosmos-db-dataflow-metadata';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ConnectionAuthKind } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

export const cosmosDbMongoDbApiMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.cosmosMongo.linkedServiceType,
    displayName: stringResources.CosmosDbMongoApi_DisplayText,
    linkedServiceKind: 'hierarchical',
    icon: {
        className: 'svg-i-100x100-AzureCosmosDB'
    },
    tableNameAlias: 'collection',
    queryAlias: 'filter',
    supportDataFlowSource: true,
    dataflowSource: [dataflowCosmosDbMongoSource],
    supportPE: true,
    peGroupIDs: ['MongoDB', 'Analytical'],
    supportedInTrident: true,
    supportCopyJobSource: true,
    supportCopyJobSink: true,
    supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
    supportAutoFillCopilotInput: true,
    isKnownInPipelineContext: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'AzureCosmosDBForMongoDB' as ResourceKind
    },
    supportOPDG: GlobalVariables.enableOPDG,
    supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
    resourceTypes: ['Microsoft.DocumentDb/databaseAccounts'],
    peWellknowErrorMessages: [
        ...cosmosDBPEWellknownErrorMessages,
        /Connection to MongoDB server is timeout/i],
    isFirstClass: true,
    supportDatasetV2: true,
    linkedServiceNamePrefix: 'CosmosDbMongoDb',
    copySinkTypes: [adfModelTypeMap.cosmosMongo.copySinkType],
    copySourceTypes: [adfModelTypeMap.cosmosMongo.copySourceType],
    category: ['Azure'],
    onetimeQueryTemplate: '{\n<<columnName>>: filterValue\n}',
    timedQueryTemplate: '{\n<<columnName>>: {\n$gte: ISODate(\'@{formatDateTime(pipeline().parameters.windowStart)}\'),\n$lt: ISODate(\'@{formatDateTime(pipeline().parameters.windowEnd)}\')}\n}',
    deltaLoadQueryTemplate: `{<<columnName>>: {
        $gte: @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
            json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
            concat('ISODate(''', formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue), ''')'))},
        $lt: @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
            concat('ISODate(''', formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue), ''')'))},
    }}`,
    controls: [
        {
            type: 'select-button-switch',
            required: true,
            isLabelVertical: true,
            options: [
                {
                    label: stringResources.ModelName_ConnectionString,
                    value: false
                },
                {
                    label: stringResources.AzureKeyVault_DisplayText,
                    value: true
                }
            ],
            bind: 'enableAKV',
            isNotRelatedToCredential: true,
            defaultValue: false,
            cases: [
                {
                    caseValue: true,
                    children: [
                        {
                            type: 'radio',
                            bind: 'isServerVersionAbove32',
                            label: stringResources.ModelName_ServerVersion,
                            description: stringResources.CosmosMongoServerDesc,
                            escapeDescription: false,
                            defaultValue: false,
                            options: server32Options,
                            isLabelVertical: true
                        },
                        {
                            type: 'azure-key-vault',
                            bind: 'referedAKV',
                            isLabelVertical: true,
                            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.CosmosDbMongoApi_DisplayText),
                                connectionString)
                        },
                        {
                            type: 'textbox',
                            bind: 'databaseName',
                            label: stringResources.ModelName_DatabaseName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    ]
                },
                {
                    caseValue: false,
                    children: [
                        {
                            type: 'radio-switch',
                            nested: 1,
                            label: stringResources.AccountSelectionMethodLabel,
                            description: stringResources.AzureCosmos_SelectMethodDescription,
                            options: azureResourceSelectMethodOptions,
                            defaultValue: '{connectionString != null ? "manual" : "azure"}',
                            isLabelVertical: true,
                            isSecret: true,
                            cases: [
                                {
                                    caseValue: 'azure',
                                    children: [
                                        {
                                            type: 'azure-cosmos-db-select',
                                            bind: {
                                                isServerVersionAbove32: 'isServerVersionAbove32',
                                                connectionString: 'connectionString',
                                                databaseName: 'databaseName',
                                                account: 'account',
                                                resourceID: '_accountResourceID'
                                            },
                                            showPEStatus: true,
                                            isCosmosDbMongoDbApi: true,
                                            validators: {
                                                connectionString: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                                account: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                                databaseName: [
                                                    { type: 'required', errorMessage: '' }
                                                ]
                                            }
                                        }
                                    ]
                                },
                                {
                                    caseValue: 'manual',
                                    children: [
                                        {
                                            type: 'mongo-db-select',
                                            bind: {
                                                isServerVersionAbove32: 'isServerVersionAbove32',
                                                connectionString: 'connectionString',
                                                databaseName: 'databaseName',
                                                account: 'account',
                                            },
                                            mongoDbType: 'cosmos' as supportedMongoDbTypes,
                                            validators: {
                                                connectionString: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                                databaseName: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                                account: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                            }
                                        },
                                        {
                                            type: 'private-endpoint-status'
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ]
};
